import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Plus } from "lucide-react";

/**
 * Searchable dropdown for CRM forms. Dark-theme, mobile-friendly.
 * - options: string[] or [{ value, label }]
 * - allowCustom: shows "Use '<query>'" row when the query has no exact match
 */
export default function SearchSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  allowCustom = false,
  loading = false,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const norm = options.map((o) =>
    typeof o === "object" && o !== null
      ? { value: o.value, label: String(o.label ?? o.value) }
      : { value: o, label: String(o) }
  );
  const q = query.trim().toLowerCase();
  const filtered = q ? norm.filter((o) => o.label.toLowerCase().includes(q)) : norm;
  const exact = q && norm.some((o) => o.label.toLowerCase() === q);
  const current = norm.find((o) => String(o.value) === String(value));

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const pick = (v) => {
    onChange(String(v));
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 30); }}
        className={`w-full flex items-center justify-between gap-2 bg-white/5 border rounded-lg px-4 py-3 text-[15px] text-left transition-colors ${
          disabled ? "opacity-40 cursor-not-allowed border-white/8" : open ? "border-[#1F5EFF]" : "border-white/12 hover:border-white/25"
        }`}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <span className={current || value ? "text-white truncate" : "text-white/35 truncate"}>
          {current ? current.label : value || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1.5 w-full rounded-xl border border-white/12 bg-[#101318] shadow-2xl shadow-black/60 overflow-hidden"
          data-testid={testid ? `${testid}-menu` : undefined}>
          <div className="p-2 border-b border-white/8">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filtered.length) pick(filtered[0].value);
                  else if (allowCustom && query.trim()) pick(query.trim());
                }
              }}
              placeholder="Type to search…"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#1F5EFF]"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {loading && <div className="px-3 py-3 text-white/40 text-sm">Loading…</div>}
            {!loading && allowCustom && q && !exact && (
              <button type="button" onClick={() => pick(query.trim())}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-[#8db2ff] hover:bg-[#1F5EFF]/10 border-b border-white/8"
                data-testid={testid ? `${testid}-custom` : undefined}>
                <Plus className="w-3.5 h-3.5" /> Use “{query.trim()}”
              </button>
            )}
            {!loading && filtered.map((o) => (
              <button key={String(o.value)} type="button" onClick={() => pick(o.value)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                  String(o.value) === String(value) ? "bg-[#1F5EFF]/15 text-white" : "text-white/80 hover:bg-white/5"
                }`}
                data-testid={testid ? `${testid}-opt-${String(o.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}>
                <span className="truncate">{o.label}</span>
                {String(o.value) === String(value) && <Check className="w-3.5 h-3.5 text-[#8db2ff] shrink-0" />}
              </button>
            ))}
            {!loading && !filtered.length && !(allowCustom && q) && (
              <div className="px-3 py-3 text-white/35 text-sm">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
